// src/admin/components/ConfirmDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from './AdminModal.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

export default function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel, variant = 'default' }) {
  const { t } = useTranslation('admin');
  const isDanger = variant === 'danger';
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Reset transient state every time the dialog opens
  useEffect(() => {
    if (open) { setLoading(false); setErr(''); }
  }, [open]);

  async function handleConfirm() {
    setErr('');
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setErr(e?.message || t('shared.error'));
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    if (loading) return; // can't dismiss while a confirm is in-flight
    onClose();
  }

  return (
    <AdminModal open={open} onClose={handleClose} title={title} width={400}>
      <p style={{ color: COLORS.ink, fontSize: '14px', margin: '0 0 16px', lineHeight: 1.5, fontFamily: FONT_SANS }}>{message}</p>
      {err && (
        <div role="alert" style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}`, color: SEMANTIC.errore.fg, fontSize: '13px', fontFamily: FONT_SANS }}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        {/* v196: both buttons are type="button" — AdminModal does not portal, so inside a <form>
            an untyped button would SUBMIT it (the personalization picker's replace dialog started the run). */}
        <button
          type="button"
          onClick={handleClose}
          disabled={loading}
          style={{
            padding: '8px 16px', borderRadius: RADII.controlInner,
            border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
            color: COLORS.ink, fontSize: '13px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
            fontFamily: FONT_SANS,
          }}
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          style={{
            padding: '8px 16px', borderRadius: RADII.controlInner, border: 'none',
            background: loading ? COLORS.borderStrong : (isDanger ? SEMANTIC.errore.fg : COLORS.accent),
            color: COLORS.surface, fontSize: '13px', fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: FONT_SANS,
          }}
        >
          {loading ? t('confirm.working') : (confirmLabel ?? t('confirm.confirm'))}
        </button>
      </div>
    </AdminModal>
  );
}
